import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { MenuModule } from '@tbtk/menu';
import { TbtkProfileModule } from '@tbtk/profile';
import { Subscription } from 'rxjs';
import SideNavData from 'src/assets/data/items/sidenav-items.json';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import * as SgeCrypto from 'src/assets/lib/sge/sge-crypto';

import { FileUploadAnimationComponent } from './components/file-upload-animation/file-upload-animation.component';
import { LoadingComponent } from './components/loading/loading.component';
import { LoadingService } from './components/loading/loading.service';
import { CreateEncryptedFolderModalComponent } from './components/modal/create-encrypted-folder-modal/create-encrypted-folder-modal.component';
import { CreateFolderModalComponent } from './components/modal/create-folder-modal/create-folder-modal.component';
import { CreateLinkModalComponent } from './components/modal/create-link-modal/create-link-modal.component';
import { DocumentTemplateComponent } from './components/modal/document-template/document-template.component';
import { DocumentTemplateService } from './components/modal/document-template/document-template.service';
import { FolderTreeModalComponent } from './components/modal/folder-tree-modal/folder-tree-modal.component';
import { ManageLinkModalComponent } from './components/modal/manage-link-modal/manage-link-modal.component';
import { ManageLinksModalComponent } from './components/modal/manage-links-modal/manage-links-modal.component';
import { MultiCreateLinkComponent } from './components/modal/multi-create-link/multi-create-link.component';
import { MultiShareModalComponent } from './components/modal/multi-share-modal/multi-share-modal.component';
import { ShareDetailsModalComponent } from './components/modal/share-details-modal/share-details-modal.component';
import { ShareManagementModalComponent } from './components/modal/share-management-modal/share-management-modal.component';
import { ShareModalComponent } from './components/modal/share-modal/share-modal.component';
import { DetailSearchComponent } from './components/search/detail/detail-search.component';
import { TopRightDropdownComponent } from './components/top-right-dropdown/top-right-dropdown.component';
import { TbtkAppBarComponent } from './libs/appbar/appbar.component';
import { TbtkHeaderComponent } from './libs/header/header.component';
import { SideNav, TbtkSideNavComponent } from './libs/sidenav/sidenav.component';
import { DownloadModel } from './models/download.model';
import { UploadModel, UserQuota } from './models/upload.model';
import { AuthStore } from './services/auth.store';
import { CommonService } from './services/common.service';
import { DownloadService } from './services/download.service';
import { ModalService } from './services/modal.service';
import { PaginationService } from './services/pagination.service';
import { PathService } from './services/path.service';
import { SearchService } from './services/search.service';
import { UploadService } from './services/upload.service';
export const pageWrapper = true;
@Component({
  selector: 'tbtk-wrapper',
  templateUrl: './page-wrapper.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [CommonService, LoadingService, PathService, DocumentTemplateService],
  standalone: true,
  imports: [
    CreateEncryptedFolderModalComponent,
    CreateFolderModalComponent,
    CreateLinkModalComponent,
    CommonModule,
    DetailSearchComponent,
    FileUploadAnimationComponent,
    FolderTreeModalComponent,
    FormsModule,
    LoadingComponent,
    ManageLinksModalComponent,
    MenuModule,
    MultiCreateLinkComponent,
    MultiShareModalComponent,
    RouterModule,
    ShareDetailsModalComponent,
    ShareManagementModalComponent,
    ShareModalComponent,
    TbtkHeaderComponent,
    TbtkProfileModule,
    TbtkSideNavComponent,
    TbtkAppBarComponent,
    TopRightDropdownComponent,
    TranslateModule,
    ManageLinkModalComponent,
    DocumentTemplateComponent,
  ],
})
export class PageWrapperComponent implements OnInit, OnDestroy {
  @ViewChild('fileInput') fileInput!: ElementRef;
  subscription: Subscription = new Subscription();
  publiclySharedSubscription: Subscription = new Subscription();
  storageSubscription: Subscription = new Subscription();
  selectionSubscription: Subscription = new Subscription();
  activatedRouteSubscription: Subscription = new Subscription();
  sidenav: SideNav = SideNavData;
  searchValue = '';
  openedModalId!: string;
  searchText = '';

  //Data
  downloadChunks: DownloadModel[] = [];
  uploadChunks: UploadModel[] = [];
  selectedItems: any[] = [];
  selectedItem: any;

  //Booleans
  isLoading = false;
  isPubliclySharedPage = false;
  isFileUploadClosed = false;
  fromFolderTree = false;
  isSafelockEnabled = false;
  securedFolder = false;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const clickedElement = event.target as HTMLElement;
    if (this.openedModalId !== '#folderTreeModal') {
      this.searchService.setDetailSearchVisibility(false);
    }
    if (this.elementRef.nativeElement.contains(event.target)) {
      this.checkExceptedElements(clickedElement);
    }
  }

  constructor(
    private activatedRoute: ActivatedRoute,
    private elementRef: ElementRef,
    private paginationService: PaginationService,
    private router: Router,
    private renderer: Renderer2,
    private uploadService: UploadService,
    public authStore: AuthStore,
    public commonService: CommonService,
    public downloadService: DownloadService,
    public loadingService: LoadingService,
    public searchService: SearchService,
    public modalService: ModalService,
    public pathService: PathService,
  ) {
    this.subscription.add(
      commonService.securedFolder$.subscribe((securedFolder) => {
        this.securedFolder = securedFolder;
      }),
    );
    this.subscription.add(authStore.isSafeLockEnabled$.subscribe((isSafeLockEnable) => (this.isSafelockEnabled = !!isSafeLockEnable)));
  }

  ngOnInit(): void {
    this.commonInit();
    this.paginationInit();
    this.quotaInit();
    this.selectionInit();
    this.uploadAndDownloadInit();
    this.routerAndModalInit();
    window.addEventListener('beforeunload', () => {
      if (this.isSafelockEnabled) {
        SgeCrypto.lockCrypto();
      }
    });
  }

  checkExceptedElements(clickedElement: HTMLElement): void {
    const parentNode = this.renderer.parentNode(clickedElement);
    setTimeout(() => {
      let card: HTMLElement | null = null;
      if (this.openedModalId !== '') {
        card = document.querySelector(this.openedModalId);
      }
      const sectionDetails = document.querySelector('#section-details');
      let exceptedElements =
        parentNode.tagName.toLowerCase() === 'app-modal' ||
        clickedElement.tagName.toLowerCase() === 'i' ||
        sectionDetails?.contains(clickedElement) ||
        card?.contains(clickedElement) ||
        this.commonService.modalFooterClicked;
      if (!(parentNode.tagName.toLowerCase() === 'div') && exceptedElements && this.commonService.modalFooterClicked) {
        exceptedElements = false;
        this.commonService.modalFooterClicked = false;
      }
      if (clickedElement && parentNode && !exceptedElements) {
        this.commonService.isDetailClicked.next(false);
        this.commonService.isShareDetailClicked.next(false);
        this.commonService.handleMultiChoice(undefined);
        this.commonService.handleSharesMultiChoice(undefined);
      }
    }, 0);
  }

  createFolder(modalId: string, fromFolderTree = false): void {
    if (this.isSafelockEnabled && this.securedFolder) {
      this.modalService.setEncryptedCreateFolderModalVisibility(true, modalId);
    } else {
      this.modalService.setCreateFolderModalVisibility(true, modalId);
    }
    this.fromFolderTree = fromFolderTree ?? false;
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.publiclySharedSubscription) {
      this.publiclySharedSubscription.unsubscribe();
    }
    if (this.storageSubscription) {
      this.storageSubscription.unsubscribe();
    }
    if (this.selectionSubscription) {
      this.selectionSubscription.unsubscribe();
    }
    if (this.activatedRouteSubscription) {
      this.activatedRouteSubscription.unsubscribe();
    }
  }

  onSearch(text: string): void {
    this.searchText = text;
    const searchText = { q: text.trim() };
    if (text.trim() !== '') {
      this.router.navigate(['/depom'], { queryParams: searchText });
    } else {
      this.router.navigate(['/depom']);
    }
  }

  getUserQuota(): void {
    this.subscription.add(
      this.commonService.getUserQuota().subscribe({
        next: (res: UserQuota) => {
          this.sidenav.quota.usage = this.commonService.convertToReadableFormat(res.usedStorage);
          this.sidenav.quota.total = this.commonService.convertToReadableFormat(res.quota);
          this.sidenav.quota.percentage = (res.usedStorage / res.quota) * 100;
        },
      }),
    );
  }

  logout(): void {
    this.authStore.logout();
  }

  closeModal(): void {
    this.isFileUploadClosed = true;
  }

  newFolderCreated(isFolderCreated: boolean, fromStorage: boolean): void {
    if (isFolderCreated) {
      this.commonService.rebindListener.next();
      if (fromStorage) {
        this.modalService.setCreateFolderModalVisibility(false);
      } else {
        this.modalService.setEncryptedCreateFolderModalVisibility(false);
      }
    }
  }

  routerAndModalInit(): void {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        if (!event.url.startsWith('/depom?q=') && event.url !== '/depom') {
          this.searchService.setIsDetailSearching(false);
          this.searchService.setSearchText('');
          this.searchService.setResetSearchForm();
        }
        this.commonService.setOpenedModalId('');
      }
    });

    this.subscription.add(
      this.activatedRoute.queryParams.subscribe((params) => {
        this.searchValue = params.q;
      }),
    );
    this.commonService.openedModalId$.subscribe((modalId) => {
      this.openedModalId = modalId;
    });
  }

  uploadAndDownloadInit(): void {
    this.downloadService.downloadItems$.subscribe((downloadItems) => {
      this.downloadChunks = downloadItems;
      this.isFileUploadClosed = false;
    });
    this.uploadService.uploadItems$.subscribe((uploadItems) => {
      this.uploadChunks = uploadItems;
      this.isFileUploadClosed = false;
    });
  }

  selectionInit(): void {
    this.selectionSubscription.add(
      this.commonService.multipleSelectedItems$.subscribe((selectedItems) => {
        this.selectedItem = undefined;

        if (selectedItems.length === 1) {
          [this.selectedItem] = selectedItems;
        }
        this.selectedItems = selectedItems;
      }),
    );
    this.selectionSubscription.add(
      this.commonService.sharesMultipleSelectedItems$.subscribe((selectedItems) => {
        this.selectedItem = undefined;
        if (selectedItems) {
          if (selectedItems && selectedItems.length === 1) {
            [this.selectedItem] = selectedItems;
          } else {
            this.selectedItems = selectedItems;
            this.commonService.showEncryptedNonEncryptedTogetgerNotification(this.selectedItems);
          }
        }
      }),
    );
  }

  quotaInit(): void {
    this.getUserQuota();
    this.subscription.add(
      this.commonService.quotaChange$.subscribe(() => {
        this.getUserQuota();
      }),
    );
    this.uploadService.uploadCompleted$.subscribe(() => {
      this.getUserQuota();
    });
  }

  paginationInit(): void {
    this.subscription.add(
      this.paginationService.resetPagination$.subscribe(() => {
        this.paginationService.loadItems();
      }),
    );
  }

  commonInit(): void {
    this.publiclySharedSubscription = this.commonService.isInPubliclyShared.subscribe((data) => {
      this.isPubliclySharedPage = data;
    });
  }
}
